import { useState } from 'react';

import { resendVerification, verifyEmail } from '../api/client.ts';
import { RouteLink } from '../studies/RouteLink.tsx';
import type { Route } from '../studies/routing.ts';
import { CheckEmailNotice } from './CheckEmailNotice.tsx';
import { EmailRequestForm } from './EmailRequestForm.tsx';
import { failureMessage, isUnusableLink } from './failure.ts';
import { useFragmentToken } from './fragment-token.ts';

export interface VerifyEmailViewProps {
  onNavigate: (route: Route) => void;
  /**
   * The address this browser is signed in as, or null when it is not (or not yet known). A link
   * for one account can be opened in a browser signed in as another, and the confirmation has to
   * say which session a "carry on" would land in.
   */
  signedInAs?: string | null;
  /** Ends the current session, after which the shell shows the sign-in form. */
  onSignOut?: () => Promise<void>;
}

type Phase = 'ready' | 'pending' | 'verified' | 'invalid';

/**
 * Where a verification link lands. The link is spent by the button, not by opening it, and a
 * confirmed address still signs nobody in: the screen says so and points at the sign-in form,
 * because the password is what opens a session and the link only proves the mailbox.
 *
 * A link that cannot be used — missing, cut short, expired or already spent, which the server does
 * not tell apart — offers to send a new one.
 */
export const VerifyEmailView = ({ onNavigate, signedInAs, onSignOut }: VerifyEmailViewProps) => {
  const token = useFragmentToken();

  // Keyed by the token, so a second link opened in this tab starts from its own confirm button
  // rather than from whatever the first one ended on.
  return (
    <VerifyEmailLink
      key={token ?? ''}
      token={token}
      onNavigate={onNavigate}
      signedInAs={signedInAs}
      onSignOut={onSignOut}
    />
  );
};

const VerifyEmailLink = ({
  token,
  onNavigate,
  signedInAs = null,
  onSignOut,
}: VerifyEmailViewProps & { token: string | null }) => {
  const [phase, setPhase] = useState<Phase>('ready');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  if (sentTo !== null) {
    return (
      <main className="login" data-test-id="verify-email-view">
        <CheckEmailNotice email={sentTo} sendAgain={resendVerification} onNavigate={onNavigate} />
      </main>
    );
  }

  const signIn = (
    <p className="login-links">
      <RouteLink to={{ name: 'studies' }} onNavigate={onNavigate} testId="verify-email-sign-in">
        Sign in
      </RouteLink>
    </p>
  );

  if (phase === 'verified' && signedInAs !== null) {
    return (
      <main className="login" data-test-id="verify-email-view">
        <h1>E-mail address confirmed</h1>
        <p data-test-id="verify-email-done" role="status">
          The e-mail address is confirmed. This browser is signed in as{' '}
          <strong data-test-id="verify-email-signed-in-as">{signedInAs}</strong>; to use the account
          you just confirmed, sign out and sign in with its password.
        </p>
        {signOutError === null ? null : (
          <p className="login-error" data-test-id="verify-email-sign-out-error" role="alert">
            {signOutError}
          </p>
        )}
        <button
          type="button"
          data-test-id="verify-email-sign-out"
          disabled={signingOut || onSignOut === undefined}
          onClick={() => {
            if (onSignOut === undefined) {
              return;
            }
            setSigningOut(true);
            setSignOutError(null);
            void onSignOut().then(
              () => setSigningOut(false),
              () => {
                setSigningOut(false);
                setSignOutError('Signing out failed. Please try again.');
              },
            );
          }}
        >
          {signingOut ? 'Signing out…' : 'Sign out and sign in'}
        </button>
        <p className="login-links">
          <RouteLink
            to={{ name: 'studies' }}
            onNavigate={onNavigate}
            testId="verify-email-continue"
          >
            Continue as {signedInAs}
          </RouteLink>
        </p>
      </main>
    );
  }

  if (phase === 'verified') {
    return (
      <main className="login" data-test-id="verify-email-view">
        <h1>E-mail address confirmed</h1>
        <p data-test-id="verify-email-done" role="status">
          Your e-mail address is confirmed. You are not signed in yet: sign in with your password to
          carry on.
        </p>
        {signIn}
      </main>
    );
  }

  if (phase === 'invalid' || token === null) {
    return (
      <main className="login" data-test-id="verify-email-view">
        <h1>This link cannot be used</h1>
        <p data-test-id="verify-email-invalid">
          It may have expired, been used already, or been copied only in part. Enter your e-mail
          address and we will send a new one.
        </p>
        <EmailRequestForm
          testIdPrefix="resend-verification"
          submitLabel="Send a new link"
          pendingLabel="Sending…"
          send={resendVerification}
          onSent={setSentTo}
        />
        {signIn}
      </main>
    );
  }

  return (
    <main className="login" data-test-id="verify-email-view">
      <h1>Confirm your e-mail address</h1>
      <p>Press the button to finish confirming the address this link was sent to.</p>

      {error === null ? null : (
        <p className="login-error" data-test-id="verify-email-error" role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        data-test-id="verify-email-confirm"
        disabled={phase === 'pending'}
        onClick={() => {
          setPhase('pending');
          setError(null);
          void verifyEmail(token).then(
            () => setPhase('verified'),
            (failure: unknown) => {
              if (isUnusableLink(failure)) {
                setPhase('invalid');

                return;
              }

              // A throttle or no connection leaves the link unspent, so the button stays to retry.
              setPhase('ready');
              setError(failureMessage(failure));
            },
          );
        }}
      >
        {phase === 'pending' ? 'Confirming…' : 'Confirm my e-mail address'}
      </button>
    </main>
  );
};
